// components/TypingEffect.js
import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useMemo,
} from "react";
import ReactMarkdown, { Options } from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css"; // KaTeX 样式
import rehypeRaw from "rehype-raw";
import plantumlEncoder from "plantuml-encoder";
import { useRouter } from "next/router";
import CodeBlock from "./CodeBlock/CodeBlock";
import MermaidChart from "./mermaid/MermaidChart";
import { title } from "process";
import { Image, ImageViewer, Space } from "antd-mobile";
import { useTheme } from "../context/ThemeContext";

const TypingEffect = (
  props: {
    text: string;
    pending?: boolean;
    stop?: boolean;
    goToBottom?: () => void;
    uuid?: string;
    thread_id?: string;
    umlReloadSuccess?: (str: string, uuid: string) => void;
    hrefDisable?: boolean;
    title?: string;
    type?: "wiki" | "eagle";
  },
  ref: any
) => {
  const {
    text = "",
    pending = false,
    goToBottom = () => {},
    uuid,
    thread_id,
    umlReloadSuccess,
    hrefDisable = false,
    title,
    type = "eagle",
  } = props;
  const { theme } = useTheme();
  const router = useRouter();
  const [isRendered, setIsRendered] = useState(false);
  const [imgLoadErr, setImgLoadErr] = useState(false);
  const [historyItem, setHistoryItem] = useState(text);
  const [imageSrc, setImageSrc] = useState("");
  const [imageVisible, setImageVisible] = useState(false);
  const umlRegex =
    /@start(uml|json|yaml|bnf|regex|salt|ditaa|gantt|chronology|mindmap|wbs|chen)[\s\S]*?@end\1/g;
  useEffect(() => {
    setHistoryItem(text);
  }, [text]);
  useEffect(() => {
    if (isRendered && thread_id === router.query.thread_id) {
      setTimeout(() => {
        goToBottom();
      }, 400);
    }
  }, [isRendered]);

  useImperativeHandle(ref, () => {
    return {};
  });
  const LinkRenderer = (data: any) => {
    const { href, children, ...args } = data;
    return hrefDisable ? (
      <a
        onClick={(e) => {
          e.preventDefault();
        }}
      >
        {children}
      </a>
    ) : (
      <a href={href} target="_blank" rel="noopener noreferrer" {...args}>
        {children}
      </a>
    );
  };
  const ImgRenderer = (data: any) => {
    const { src, children, ...args } = data;
    return <Image src={src} alt="" onClick={() => handlePreview(src)} />;
  };
  const ImageRender = (url: any) => {
    const urlList = JSON.parse(localStorage.getItem("sUrl") || "[]");
    if (!urlList?.includes(url)) {
      const fetchImage = async () => {
        try {
          setIsRendered(false);
          const response = await fetch(url, { method: "HEAD" });
          if (response.status !== 200) {
            setImgLoadErr(true);
          } else {
            localStorage.setItem("sUrl", JSON.stringify([...urlList, url]));
            setIsRendered(true);
          }
        } catch (error) {
          setImgLoadErr(true);
        }
      };
      fetchImage();
    }
    // 使用useMemo来记忆计算结果
    const memoizedValue = useMemo(
      () => (
        <Image
          key={url}
          src={url}
          onClick={() => handlePreview(url)}
          onError={() => {
            setImgLoadErr(true);
          }}
          alt=""
        />
      ),
      [url]
    );

    return memoizedValue;
  };
  const handlePreview = (src: string) => {
    setImageVisible(true);
    setImageSrc(src);
  };
  const reloadSuccess = (str: string, uuid?: string) => {
    if (type === "wiki") {
      setHistoryItem(str);
    } else {
      umlReloadSuccess?.(str, uuid || "");
    }
  };
  const MermaidRenderer = (
    children: any,
    uuid: any,
    thread_id: any,
    historyItem: string,
    title?: string,
    type?: "wiki" | "eagle"
  ) => {
    const Mermaid = useMemo(
      () => (
        <MermaidChart
          chart={children}
          uuid={uuid}
          thread_id={thread_id}
          gotoBottom={goToBottom}
          historyItem={historyItem}
          title={title}
          type={type}
          umlReloadSuccess={reloadSuccess}
        ></MermaidChart>
      ),
      [children, historyItem, thread_id, title, type, uuid]
    );
    return Mermaid;
  };
  const CodeBlockRenderer = (
    children: any,
    node?: any,
    arg?: any,
    simple?: boolean,
    title?: string
  ) => {
    const Mermaid = useMemo(
      () => (
        <CodeBlock node={node} simple={simple} arg={arg} title={title}>
          {children}
        </CodeBlock>
      ),
      [node, simple, arg, children]
    );
    return Mermaid;
  };
  return (
    <>
      <ReactMarkdown
        remarkPlugins={[
          remarkGfm,
          [remarkMath, { singleDollarTextMath: true }],
        ]}
        rehypePlugins={[
          rehypeRaw,
          rehypeKatex,
          [
            rehypeHighlight,
            {
              ignoreMissing: false,
              plainText: ["text", "plain", "txt"],
            },
          ],
        ]}
        components={{
          pre: ({ children }) => <pre className="not-prose">{children}</pre>,
          a: LinkRenderer,
          img: ImgRenderer,
          code: (data: any) => {
            let { node, className, children, ...props } = data;
            if (
              !className &&
              typeof children === "string" &&
              children.includes("\n")
            ) {
              className = "hljs language-txt";
            }
            const match = /language-(\w+)/.exec(className || "");
            if (match && match?.length) {
              const isPlantUml =
                match.includes("language-plantuml") ||
                match.includes("plantuml") ||
                match.includes("plaintext") ||
                match.includes("language-plaintext");
              const isMermaid =
                match.includes("language-mermaid") || match.includes("mermaid");
              const umlMatches = umlRegex.test(children);
              if (isPlantUml && umlMatches && !imgLoadErr && !pending) {
                const match = children.match(umlRegex);
                const encoded = plantumlEncoder.encode(match?.[0] || "");
                const url = `https://eagle.capitalren.com/plant-uml/png/${encoded}`;
                return ImageRender(url);
              }
              if (isMermaid && !pending) {
                return MermaidRenderer(
                  children,
                  uuid,
                  thread_id,
                  historyItem,
                  title,
                  type
                );
              }
              return CodeBlockRenderer(
                children,
                node,
                { ...props },
                false,
                match[1] || ""
              );
            } else {
              return CodeBlockRenderer(children, node, { ...props }, true);
            }
          },
        }}
        className={`max-w-full overflow-x-auto prose ${
          theme === "light" ? "prose-zinc" : "prose-invert"
        }`}
      >
        {text}
      </ReactMarkdown>
      <ImageViewer
        classNames={{
          mask: "customize-mask",
          body: "customize-body",
        }}
        image={imageSrc}
        visible={imageVisible && !pending}
        onClose={() => {
          setImageVisible(false);
        }}
      />
    </>
  );
};

// 用 React.memo 包裹 TypingEffect 组件，提升渲染性能
const MemoTypingEffect = React.memo(forwardRef(TypingEffect));

export default MemoTypingEffect;
